import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

import Footer from "src/components/wizard/Footer/Footer";
import Header from "src/components/wizard/Header";
import SearchButton from "src/components/wizard/SearchButton";
import SearchTextField from "src/components/wizard/SearchTextField";
import useInventoryItemsFilter from "src/hooks/useInventoryItemsFilter";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_REQUEST_MUTATION,
} from "src/constants/graphql/mutations";
import {
  createEmptyLocation,
  ReturnConfirmStep,
  ReturnDestinationStep,
  ReturnSelectionStep,
} from "./ReturnItemsModalSteps";

const STEP_SELECT = 0;
const STEP_DESTINATION = 1;
const STEP_CONFIRM = 2;
const TOTAL_STEPS = 3;

const STEP_TITLES = {
  [STEP_SELECT]: "Select Items to Return",
  [STEP_DESTINATION]: "Return Locations",
  [STEP_CONFIRM]: "Confirm Return",
};

const DESTINATION_SUMMARIES = {
  original: "Original pick locations",
  shared: "One shared return location",
  individual: "A return location selected for each picked item",
};

function locationIsSelected(location) {
  return Boolean(location?.warehouse?.id && location?.warehouse_locations?.[0]?.id);
}

function itemLabel(count) {
  return `${count} item${count === 1 ? "" : "s"}`;
}

function buildReturnLocations(item, destination) {
  const baseLocation = {
    warehouse: destination.warehouse,
    warehouse_locations: destination.warehouse_locations,
    mobile_warehouse_location: destination.mobile_warehouse_location,
    allocated_quantity: 0,
  };

  if (item.asset?.type === "Kit") {
    return (item.rackLocations || []).map((location) => ({
      ...baseLocation,
      asset_id: location.asset_id,
      sub_qty: location.sub_qty,
    }));
  }

  return [
    {
      ...baseLocation,
      asset_id: item.asset?.id || item.assetId,
      sub_qty: item.quantity,
    },
  ];
}

export default function ReturnItemsModal({ inventoryRequest, onClose, onReturned }) {
  const [step, setStep] = useState(STEP_SELECT);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [destinationMode, setDestinationMode] = useState("original");
  const [sharedLocation, setSharedLocation] = useState(createEmptyLocation);
  const [itemLocations, setItemLocations] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [returnedCount, setReturnedCount] = useState(0);
  const { notifySuccess, notifyError } = useNotification();
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);

  const inventoryItems = inventoryRequest?.inventoryItems || [];
  const returnableItems = useMemo(
    () => inventoryItems.filter((item) => !item.requiredCompanion),
    [inventoryItems]
  );
  const companionCount = useMemo(
    () => inventoryItems.filter((item) => item.requiredCompanion).length,
    [inventoryItems]
  );
  const selectedItems = useMemo(
    () => returnableItems.filter((item) => selectedIds.includes(item.id)),
    [returnableItems, selectedIds]
  );
  const selectedPickedItems = useMemo(
    () => selectedItems.filter((item) => item.pickedStatus),
    [selectedItems]
  );
  const { searchTerm, filteredItems, handleSearchChange } =
    useInventoryItemsFilter(returnableItems);

  const selectedCount = selectedItems.length;
  const allSelected = selectedCount > 0 && selectedCount === returnableItems.length;
  const wouldEmptyOrder =
    selectedCount > 0 &&
    inventoryItems.every((item) => selectedIds.includes(item.id) || item.requiredCompanion);
  const destinationIsValid =
    destinationMode === "original" ||
    (destinationMode === "shared" && locationIsSelected(sharedLocation)) ||
    (destinationMode === "individual" &&
      selectedPickedItems.every((item) => locationIsSelected(itemLocations[item.id])));

  const handleToggleItem = (itemId) => {
    setSelectedIds((previous) =>
      previous.includes(itemId) ? previous.filter((id) => id !== itemId) : [...previous, itemId]
    );
  };

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? [] : returnableItems.map((item) => item.id));
  };

  const handleToggleSearch = () => {
    if (showSearch) handleSearchChange({ target: { value: "" } });
    setShowSearch((previous) => !previous);
  };

  const handleNextFromSelection = () => {
    setStep(selectedPickedItems.length > 0 ? STEP_DESTINATION : STEP_CONFIRM);
  };

  const handleBack = () => {
    if (step === STEP_CONFIRM && selectedPickedItems.length === 0) {
      setStep(STEP_SELECT);
    } else {
      setStep((previous) => Math.max(previous - 1, STEP_SELECT));
    }
  };

  const handleItemLocationChange = (itemId, values) => {
    setItemLocations((previous) => ({
      ...previous,
      [itemId]: { ...(previous[itemId] || createEmptyLocation()), ...values },
    }));
  };

  const appendReasonToFinalReleaseNotes = async () => {
    const existingNotes = inventoryRequest?.finalReleaseNotes?.trim();
    const returnNote = `Returned ${itemLabel(selectedCount)} to stock. Reason: ${reason.trim()}`;

    await updateInventoryRequest({
      variables: {
        id: inventoryRequest.id,
        input: {
          finalReleaseNotes: existingNotes ? `${existingNotes}\n\n${returnNote}` : returnNote,
        },
      },
    });
  };

  const handleReturn = async () => {
    setSaving(true);
    setReturnedCount(0);
    const completedIds = [];

    try {
      for (const item of selectedItems) {
        const variables = { id: item.id };
        if (item.pickedStatus && destinationMode !== "original") {
          const destination =
            destinationMode === "shared" ? sharedLocation : itemLocations[item.id];
          variables.rackLocations = buildReturnLocations(item, destination);
        }

        const response = await deleteInventoryItem({ variables });
        if (!response?.data?.deleteInventoryItem?.success) {
          throw new Error("Failed to return item");
        }

        completedIds.push(item.id);
        setReturnedCount(completedIds.length);
      }

      await appendReasonToFinalReleaseNotes();
      notifySuccess(`Returned ${itemLabel(selectedCount)} to stock`);
      if (onReturned) await onReturned();
      onClose();
    } catch {
      setSelectedIds((previous) => previous.filter((id) => !completedIds.includes(id)));
      if (completedIds.length > 0 && onReturned) await onReturned();
      setStep(STEP_SELECT);
      const errorMessage =
        completedIds.length > 0
          ? `Returned ${itemLabel(completedIds.length)}. The remaining items were not changed.`
          : "Failed to return items to stock";
      notifyError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      onClose={saving ? undefined : onClose}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: 720,
          height: "min(820px, 90vh)",
          overflow: "hidden",
        },
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Header
          title={STEP_TITLES[step]}
          actions={
            step === STEP_SELECT ? (
              <SearchButton open={showSearch} onClick={handleToggleSearch} />
            ) : null
          }
          info={
            step === STEP_SELECT ? (
              <Typography variant="body2" color="textSecondary">
                {selectedCount} selected
              </Typography>
            ) : null
          }
          onClose={saving ? undefined : onClose}
          sticky
        />
        <Box height={3}>{saving && <LinearProgress />}</Box>
        {showSearch && step === STEP_SELECT && (
          <Box px={2} pt={1.5}>
            <SearchTextField value={searchTerm} onChange={handleSearchChange} />
          </Box>
        )}
        {step === STEP_SELECT && (
          <ReturnSelectionStep
            allSelected={allSelected}
            companionCount={companionCount}
            filteredItems={filteredItems}
            onToggleAll={handleToggleAll}
            onToggleItem={handleToggleItem}
            returnableItemCount={returnableItems.length}
            searchTerm={searchTerm}
            selectedIds={selectedIds}
            wouldEmptyOrder={wouldEmptyOrder}
          />
        )}
        {step === STEP_DESTINATION && (
          <ReturnDestinationStep
            destinationMode={destinationMode}
            itemLocations={itemLocations}
            onDestinationModeChange={setDestinationMode}
            onItemLocationChange={handleItemLocationChange}
            onSharedLocationChange={(values) =>
              setSharedLocation((previous) => ({ ...previous, ...values }))
            }
            saving={saving}
            selectedPickedItems={selectedPickedItems}
            sharedLocation={sharedLocation}
          />
        )}
        {step === STEP_CONFIRM && (
          <ReturnConfirmStep
            destinationSummary={DESTINATION_SUMMARIES[destinationMode]}
            onReasonChange={setReason}
            reason={reason}
            returnedCount={returnedCount}
            saving={saving}
            selectedCount={selectedCount}
            selectedPickedCount={selectedPickedItems.length}
          />
        )}
        <Footer step={step} steps={TOTAL_STEPS} showRaceMode={false}>
          <Box display="flex" gap={1}>
            {step === STEP_SELECT ? (
              <Button variant="outlined" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
            ) : (
              <Button variant="outlined" onClick={handleBack} disabled={saving}>
                Back
              </Button>
            )}
            {step === STEP_SELECT && (
              <Button
                variant="contained"
                onClick={handleNextFromSelection}
                disabled={selectedCount === 0 || wouldEmptyOrder}
              >
                Next
              </Button>
            )}
            {step === STEP_DESTINATION && (
              <Button
                variant="contained"
                onClick={() => setStep(STEP_CONFIRM)}
                disabled={!destinationIsValid}
              >
                Next
              </Button>
            )}
            {step === STEP_CONFIRM && (
              <Button
                variant="contained"
                onClick={handleReturn}
                disabled={!reason.trim() || saving}
              >
                Return {selectedCount} {selectedCount === 1 ? "Item" : "Items"}
              </Button>
            )}
          </Box>
        </Footer>
      </Box>
    </Dialog>
  );
}
